import { logger } from '../utils/logger';
import React, { useState, useEffect, useMemo, useCallback, memo, lazy, Suspense, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Receipt,
  Warning,
  CheckCircle,
  Refresh,
  Calculate,
  AttachMoney,
  Business as BusinessIcon,
  Store as ShopifyIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import platformService from '../services/platformService';
import { runCalculateAllTax, taxCalculationErrorMessage } from '../utils/taxCalculation';
import { companyService } from '../services/companyService';
import {
  handleQuickBooksImport as sharedHandleQuickBooksImport,
} from '../utils/quickbooksImport';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CompanySelector from './CompanySelector';
import { ImportTransactionsDialog } from './ImportTransactionsDialog';
import { ImportDateRangeControls } from './ImportDateRangeControls';
import { useSharedImportDateRange } from '../hooks/useSharedImportDateRange';
import { HolisticStats, buildHolisticStats } from '../utils/holisticStats';

// Lazy load heavy components for better performance
const AllTransactions = lazy(() => import('../pages/transactions/AllTransactions'));

const API_TIMEOUTS = {
  default: 20000,
  integrations: 20000,
  platformTotals: 18000,
  platformStatus: 15000,
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = memo(function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`holistic-tabpanel-${index}`}
      aria-labelledby={`holistic-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
});

interface PlatformData {
  id: number;
  company_id: number;
  platform_name?: string;
  platform: {
    name: string;
    description: string;
  };
  store_id: string;
  is_active: boolean;
  last_sync_at: string | null;
  transaction_stats: {
    total_transactions: number;
    tax_calculated: number;
    pending_tax_calculation: number;
    amount_sum?: number;
    tax_sum?: number;
    by_status: Record<string, number>;
  };
  recent_transactions: Array<{
    id: number;
    platform_transaction_id: string;
    transaction_type: string;
    transaction_date: string;
    amount: number;
    tax_amount: number;
    customer_name: string;
    status: string;
  }>;
}

interface LoadDashboardOptions {
  companyId?: number;
  forceNetwork?: boolean;
}

const CompleteTaxView: React.FC = () => {
  const [platformData, setPlatformData] = useState<PlatformData[]>([]);
  const [holisticStats, setHolisticStats] = useState<HolisticStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number>(0);
  
  // Import/Integration state
  const [companies, setCompanies] = useState<any[]>([]);
  const [integrationDialogOpen, setIntegrationDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const {
    range: importDateRange,
    setRange: setImportDateRange,
    error: importDateRangeError
  } = useSharedImportDateRange();
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [companiesLoading, setCompaniesLoading] = useState<boolean>(true);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const dashboardLoadingRef = useRef(false);

  // Memoized calculations for performance (must be before early returns)
  const platformDataSummary = useMemo(() => ({
    total_integrations: platformData.length,
    total_transactions: platformData.reduce((sum, p) => sum + (p.recent_transactions?.length || 0), 0),
    platforms: platformData.map(p => p.platform?.name)
  }), [platformData]);

  const formatCurrency = useCallback((amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      setCompaniesLoading(true);
      setCompaniesError(null);
      const companiesData = await companyService.listCompanies();
      setCompanies(companiesData);
      
      // Set default company selection based on company count
      if (companiesData.length > 1) {
        // Multiple companies: default to "All Companies" (0)
        const storedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '0');
        if (storedCompanyId && companiesData.some(c => c.id === storedCompanyId)) {
          // Use stored value if it's valid
          setSelectedCompanyId(storedCompanyId);
        } else {
          // Default to "All Companies"
          setSelectedCompanyId(0);
          localStorage.setItem('selectedCompanyId', '0');
        }
      } else if (companiesData.length === 1) {
        // Single company: default to that company
        setSelectedCompanyId(companiesData[0].id);
        localStorage.setItem('selectedCompanyId', String(companiesData[0].id));
      } else {
        // No companies: keep 0
        setSelectedCompanyId(0);
      }

      return companiesData;
    } catch (err) {
      logger.error('Error loading companies:', err);
      setCompanies([]);
      setCompaniesError('Failed to load companies');
      throw err;
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  // useEffect will be added after loadDashboardData is defined

  // Helper to ensure error is always a string
  const getErrorMessage = useCallback((err: any) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err instanceof Error) return err.message;
    if (err?.message) return String(err.message);
    return 'An error occurred';
  }, []);

  // Helper function to add timeout to API calls
  const withTimeout = useCallback(<T,>(promise: Promise<T>, timeoutMs: number = API_TIMEOUTS.default): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
      )
    ]);
  }, []);

  const computeHolisticStats = useCallback((integrations: PlatformData[], companyId?: number) => {
    const stats = buildHolisticStats(integrations, companyId);
    setHolisticStats(stats);
    return stats;
  }, []);

  const loadDashboardData = useCallback(async (options: LoadDashboardOptions = {}) => {
    const { companyId: companyOverride, forceNetwork = false } = options;
    if (dashboardLoadingRef.current) {
      logger.log('[UI] Dashboard load already in progress, skipping duplicate request');
      return;
    }

    dashboardLoadingRef.current = true;

    try {
      setLoading(true);
      setError(null);
      logger.log('[UI] Loading dashboard data...');
      
      const effectiveCompanyId = typeof companyOverride === 'number' ? companyOverride : selectedCompanyId;
      const companyIdToUse = effectiveCompanyId === 0 ? undefined : (effectiveCompanyId || undefined);
      const actualId = typeof companyIdToUse === 'number' ? companyIdToUse : undefined;

      const integrations = await withTimeout(
        platformService.listIntegrations(
          typeof actualId === 'number' ? actualId : undefined,
          forceNetwork ? { forceRefresh: true } : {}
        ),
        API_TIMEOUTS.integrations
      ).catch(err => {
        logger.warn('[UI] Failed to load integrations, using empty array:', err);
        return [];
      });
      
      logger.log(`[UI] ${integrations.length} integrations loaded`);
      
      const validIntegrations = integrations.filter(integration => 
        integration.platform?.name && integration.is_active !== false
      );
      
      if (validIntegrations.length !== integrations.length) {
        logger.log(`[UI] Filtered ${integrations.length - validIntegrations.length} inactive/invalid integrations`);
      }
      
      const detailedData: PlatformData[] = validIntegrations.map(integration => ({
        ...integration,
        recent_transactions: integration.recent_transactions || []
      }));
      
      setPlatformData(detailedData);
      
      const companyIdForStats = effectiveCompanyId === 0 ? undefined : effectiveCompanyId;
      logger.log(`[loadDashboardData] Computing holistic stats from integration payload (companyId=${companyIdForStats ?? 'ALL'})`);
      computeHolisticStats(detailedData, companyIdForStats);
      
      logger.log('[UI] Dashboard data loaded successfully');
      
    } catch (err) {
      logger.error('[UI] Critical error loading dashboard data:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
      dashboardLoadingRef.current = false;
    }
  }, [selectedCompanyId, withTimeout, getErrorMessage, computeHolisticStats]);

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        await loadCompanies();
        if (isMounted) {
          await loadDashboardData();
        }
      } catch (err) {
        logger.error('[UI] Initialization failed:', err);
      }
    };

    initialize();

    const handler = (e: any) => {
      const totals = (e && e.detail) || {};
      setHolisticStats(prev => prev ? { ...prev, totalAmount: Number(totals.amount_sum || prev.totalAmount), totalTaxAmount: Number(totals.tax_sum || prev.totalTaxAmount) } : prev);
    };

    window.addEventListener('transactions:totals', handler);
    
    // Check for QuickBooks connection success from OAuth callback
    const quickbooksParam = searchParams.get('quickbooks');
    if (quickbooksParam === 'connected') {
      logger.log('[CompleteTaxView] QuickBooks connection callback detected');
      // Clear the URL parameter
      setSearchParams({}, { replace: true });
      // Verify integration exists before showing success message
      setTimeout(async () => {
        try {
          const companyIdToUse = selectedCompanyId || undefined;
          platformService.invalidateIntegrationsCache(
            typeof companyIdToUse === 'number' ? companyIdToUse : undefined
          );
          const integrations = await platformService.listIntegrations(
            typeof companyIdToUse === 'number' ? companyIdToUse : undefined,
            { forceRefresh: true }
          );
          const hasQuickBooks = integrations.some(
            (i: any) => i.platform?.name?.toLowerCase() === 'quickbooks' && 
                        i.is_active && 
                        i.access_token
          );
          
          if (hasQuickBooks) {
            logger.log('[CompleteTaxView] QuickBooks integration verified - showing success message');
            setSuccess('QuickBooks connection successful!');
            // Auto-hide after 5 seconds
            setTimeout(() => setSuccess(null), 5000);
          } else {
            logger.warn('[CompleteTaxView] QuickBooks integration not found after OAuth callback');
            setError('QuickBooks connection completed but integration not found. Please try again or contact support.');
          }
        } catch (err) {
          logger.error('[CompleteTaxView] Error verifying QuickBooks integration:', err);
        }
      }, 1000);
    }

    return () => {
      isMounted = false;
      window.removeEventListener('transactions:totals', handler);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshCompanies = useCallback(async () => {
    try {
      await loadCompanies();
    } catch (err) {
      logger.error('[UI] Company refresh failed:', err);
    }
  }, [loadCompanies]);

  useEffect(() => {
    if (companiesLoading) {
      return;
    }
    loadDashboardData();
  }, [selectedCompanyId, loadDashboardData, companiesLoading]);

  // Poll for per-platform totals change (CSV) and refresh when they update
  const pollForCsvTotalsChange = async (timeoutMs = 9000, intervalMs = 1000) => {
    const startAt = Date.now();
    let initialTotals: Record<string, number> | null = null;
    while (Date.now() - startAt < timeoutMs) {
      try {
        const r: any = await platformService.getAllTransactions({ skip: 0, limit: 1, platform: 'CSV', ...(selectedCompanyId ? { companyId: selectedCompanyId } : {}) } as any);
        const totals = (r?.totals || {}) as Record<string, number>;
        if (!initialTotals) {
          initialTotals = totals;
        } else {
          const changed =
            Number(totals.tax_sum ?? 0) !== Number(initialTotals.tax_sum ?? 0) ||
            Number(totals.amount_sum ?? 0) !== Number(initialTotals.amount_sum ?? 0);
          if (changed) {
            await loadDashboardData({ companyId: selectedCompanyId || undefined, forceNetwork: true });
            return true;
          }
        }
      } catch (e) {
        // ignore and continue polling briefly
      }
      await new Promise((res) => setTimeout(res, intervalMs));
    }
    return false;
  };

  // NOTE: Calculate All Tax should behave the same as in All Transactions
  // Use the unified backend endpoint and refresh dashboard data. The endpoint only starts a
  // background job, so we wait for it to settle and show what happened (or why it failed)
  // instead of a bare "started" message.
  const handleCalculateAllTax = async () => {
    if (selectedCompanyId === 0 && companies.length > 1) {
      setError('Please select a specific company before calculating tax.');
      return;
    }
    try {
      setRefreshing(true);
      setError(null);
      setSuccess(null);
      logger.log('[UI_TAX] Calculate Tax All button clicked');
      const effectiveCompanyId = selectedCompanyId || user?.primary_company_id || null;
      const outcome = await runCalculateAllTax({
        companyId: effectiveCompanyId,
        start: (options) => platformService.calculateAllTax(options),
        status: (companyId) => platformService.getTaxCalculationStatus(companyId),
      });
      if (outcome.status === 'error' || outcome.status === 'failed') {
        setError(outcome.message);
      } else {
        setSuccess(outcome.message);
      }
      // Reload data after calculation
      logger.log('[UI_TAX] Reloading dashboard data after tax calculation');
      platformService.invalidateIntegrationsCache(
        typeof effectiveCompanyId === 'number' ? effectiveCompanyId : undefined
      );
      setTimeout(() => loadDashboardData({ companyId: effectiveCompanyId ?? undefined, forceNetwork: true }), 1500);
      logger.log('[UI_TAX] Dashboard data initial reload scheduled');
      // Then poll briefly for CSV totals to update so summary reflects new tax without extra manual refresh
      await pollForCsvTotalsChange(9000, 1000);
    } catch (error) {
      logger.error('[UI_TAX] Error calculating tax:', error);
      setError(taxCalculationErrorMessage(error));
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefresh = async (companyIdOverride?: number) => {
    setRefreshing(true);
    try {
      const effectiveCompanyId = typeof companyIdOverride === 'number' ? companyIdOverride : selectedCompanyId;
      logger.log(`[UI] handleRefresh start for companyId=${effectiveCompanyId}`);

      // First refresh the dashboard data to get latest integrations
      await loadDashboardData({ companyId: effectiveCompanyId, forceNetwork: true });

      // Then sync transactions from all active integrations for the effective company
      const companyFilter = effectiveCompanyId === 0 ? undefined : effectiveCompanyId || undefined;
      const integrationsForSync = await withTimeout(
        platformService.listIntegrations(
          typeof companyFilter === 'number' ? companyFilter : undefined,
          { forceRefresh: true }
        ),
        API_TIMEOUTS.integrations
      ).catch(err => {
        logger.warn('[UI] Failed to fetch integrations during refresh, falling back to existing state:', err);
        return null;
      });

      const integrationsSource = Array.isArray(integrationsForSync) && integrationsForSync.length > 0
        ? integrationsForSync
        : platformData;

      const activeIntegrations = integrationsSource.filter((integration: any) => integration && integration.is_active);
      logger.log(`[UI] Syncing transactions from ${activeIntegrations.length} active integrations for companyId=${effectiveCompanyId}`);

      // Import transactions from the last 90 days
      const endDate = new Date();
      const startDate = new Date(endDate);
      startDate.setDate(endDate.getDate() - 90);
      const startISO = startDate.toISOString().split('T')[0];
      const endISO = endDate.toISOString().split('T')[0];

      for (const integration of activeIntegrations) {
        if (!integration || !integration.id) continue;
        try {
          logger.log(`[UI] Syncing transactions for integration ${integration.id} (${integration.platform?.name})`);
          await platformService.syncTransactions(integration.id, {
            startDate: startISO,
            endDate: endISO,
            background: true
          });
        } catch (syncError) {
          logger.warn(`[UI] Failed to sync integration ${integration?.id}:`, syncError);
          // Continue with other integrations even if one fails
        }
      }

      // Wait a bit and refresh data again to show new transactions
      platformService.invalidateIntegrationsCache(
        typeof effectiveCompanyId === 'number' ? effectiveCompanyId : undefined
      );
      setTimeout(async () => {
        await loadDashboardData({ companyId: effectiveCompanyId, forceNetwork: true });
      }, 2000);

    } catch (error) {
      logger.error('[UI] Error during refresh:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // removed duplicate handler

  const handleRetryFailedTransactions = async () => {
    if (selectedCompanyId === 0 && companies.length > 1) {
      setError('Please select a specific company before retrying failed transactions.');
      return;
    }
    try {
      setRefreshing(true);
      setError(null);
      setSuccess(null);
      logger.log('[UI_TAX] Retry Failed Transactions button clicked');
      const effectiveCompanyId = selectedCompanyId || user?.primary_company_id || null;
      const result = await platformService.retryFailedTransactions({
        companyId: effectiveCompanyId || undefined,
        background: false,
      });
      logger.log('[UI_TAX] Retry failed transactions result:', result);
      
      // Show success message
      const summary = result as Record<string, any>;
      const successCount = summary.success_count ?? 0;
      const failedCount = summary.error_count ?? 0;
      const pendingCount = summary.pending_count ?? 0;
      setSuccess(
        `Retry processed ${summary.transactions_processed ?? 0} transactions: ${successCount} succeeded, ${pendingCount} pending, ${failedCount} failed.`
      );
      
      // Reload data after retry
      logger.log('[UI_TAX] Reloading dashboard data after retry');
      platformService.invalidateIntegrationsCache(
        typeof effectiveCompanyId === 'number' ? effectiveCompanyId : undefined
      );
      setTimeout(() => loadDashboardData({ companyId: effectiveCompanyId ?? undefined, forceNetwork: true }), 2000);
      logger.log('[UI_TAX] Dashboard data reload scheduled');
      
    } catch (error) {
      logger.error('[UI_TAX] Error retrying failed transactions:', error);
      setError('Failed to retry failed transactions. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };
  // Import handlers for different platforms
  const handleQuickBooksImport = async () => {
    if (!user || !user.id || !user.email) {
      setError('You must be logged in to connect QuickBooks.');
      logger.error('[CompleteTaxView] QuickBooks connect failed: User not properly authenticated', { user });
      return;
    }
    
    const targetCompanyId = selectedCompanyId || user.primary_company_id;
    if (!targetCompanyId) {
      setError('No company associated with your account. Please set up your company first.');
      logger.error('[CompleteTaxView] QuickBooks connect failed: No company_id', { user, selectedCompanyId });
      return;
    }
    
    if (importDateRangeError) {
      setError(importDateRangeError);
      return;
    }
    setImporting(true);
    setError(null);
    
    try {
      logger.log('[CompleteTaxView] Starting QuickBooks OAuth flow', { 
        userId: user.id, 
        email: user.email, 
        companyId: targetCompanyId 
      });
      
      // This will redirect to QuickBooks OAuth - success will be shown after callback
      await sharedHandleQuickBooksImport({
        source: 'complete-tax-view',
        user: {
          id: user.id,
          email: user.email,
          company_id: targetCompanyId
        },
        setUploadError: setError,
        setImporting,
        navigate,
        companyId: targetCompanyId,
        dateRange: importDateRange
      });
      // Note: Code after this point won't execute as user will be redirected to QuickBooks
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      logger.error('[CompleteTaxView] QuickBooks connect error:', err);
      setImporting(false);
      setIntegrationDialogOpen(false);
    }
  };
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Complete Tax View
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6">Summary</Typography>
                <Typography color="text.secondary">Loading holistic stats…</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1">Recent Transactions</Typography>
              <Box sx={{ height: 200, mt: 2 }} className="MuiTable-root" />
              <LinearProgress sx={{ mt: 2 }} />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {typeof error === 'string' ? error : getErrorMessage(error)}
      </Alert>
    );
  }

  // Removed verbose UI render logging for production hardening
  return (
    <Box className="gt-page gt-page-wide">
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2, gap: 1.5, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" component="h1" className="gt-title">
            Complete tax view
          </Typography>
          <Typography variant="body1" color="text.secondary" className="gt-desc">
            Every transaction, the tax on it and where you stand on nexus, in one place.
          </Typography>
        </Box>
        {/* Wrap on narrow screens: in one row the action buttons ran off-screen at 390px and could not be tapped. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' }, '& > .MuiButton-root': { flex: { xs: '1 1 auto', sm: '0 0 auto' } } }}>
          <CompanySelector
            selectedCompanyId={selectedCompanyId}
            onCompanyChange={(cid) => {
              // Company selection change handled; verbose logging removed
              setSelectedCompanyId(cid);
              try {
                localStorage.setItem('selectedCompanyId', String(typeof cid === 'number' ? cid : 0));
              } catch {}
              // Use handleRefresh to reload data for the newly selected company
              handleRefresh(cid ?? undefined);
              try { window.dispatchEvent(new Event('transactions:refresh')); } catch {}
            }}
            label="Company"
            companies={companies}
            loading={companiesLoading}
            error={companiesError}
          onRefresh={() => {
            refreshCompanies();
          }}
            includeAllOption={companies.length > 1}
            disabled={companiesLoading || companies.length <= 1}
          />
          <Tooltip title="Refresh Data">
            <IconButton onClick={() => handleRefresh()} disabled={refreshing}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button 
            variant="outlined" 
            startIcon={<BusinessIcon />}
            onClick={handleQuickBooksImport}
            disabled={importing || refreshing || Boolean(importDateRangeError)}
            color="primary"
            sx={{ mr: 1 }}
          >
            Connect QuickBooks
          </Button>
          
          <Button 
            variant="outlined" 
            startIcon={<ShopifyIcon />}
            onClick={() => setIntegrationDialogOpen(true)}
            disabled={importing || refreshing}
            color="secondary"
            sx={{ mr: 1 }}
          >
            Connect Shopify
          </Button>
          
          <Button 
            variant="outlined" 
            startIcon={<UploadIcon />}
            onClick={() => setIntegrationDialogOpen(true)}
            disabled={importing || refreshing}
            color="info"
            sx={{ mr: 1 }}
          >
            Upload CSV
          </Button>
          <Button
            variant="contained"
            startIcon={refreshing ? <CircularProgress size={20} /> : <Calculate />}
            onClick={handleCalculateAllTax}
            disabled={refreshing}
            sx={{ mr: 1 }}
          >
            {refreshing ? 'Calculating...' : 'Calculate All Tax'}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={refreshing ? <CircularProgress size={20} /> : <Refresh />}
            onClick={handleRetryFailedTransactions}
            disabled={refreshing}
            sx={{ mr: 1 }}
          >
            {refreshing ? 'Retrying...' : 'Retry Failed Transactions'}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => window.location.href = '/platforms/integrations'}
            sx={{ ml: 1 }}
          >
            Manage Integrations
          </Button>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 560, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Import sync date range
        </Typography>
        <ImportDateRangeControls
          range={importDateRange}
          onChange={setImportDateRange}
          helperText="Defaulting to the last 365 days if left unchanged."
          disabled={importing || refreshing}
          errorText={importDateRangeError}
        />
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {typeof error === 'string' ? error : getErrorMessage(error)}
        </Alert>
      )}

      {/* Progress indicator for Calculate All Tax */}
      {refreshing && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {/* Summary Cards - compact */}
      {holisticStats && (
        <Box sx={{ mb: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(6, 1fr)' }, columnGap: 1.5, rowGap: 1.5 }}>
          <Box>
            <Card elevation={1} sx={{ p: 1 }}>
              <CardContent sx={{ py: 1 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" variant="caption" gutterBottom>
                      Total Transactions
                    </Typography>
                    <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
                      {holisticStats.totalTransactions}
                    </Typography>
                  </Box>
                  <Receipt color="primary" />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card elevation={1} sx={{ p: 1 }}>
              <CardContent sx={{ py: 1 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" variant="caption" gutterBottom>
                      Tax Calculated
                    </Typography>
                    <Typography variant="h5" color="success.main" fontWeight={700} lineHeight={1.2}>
                      {holisticStats.totalTaxCalculated}
                    </Typography>
                  </Box>
                  <CheckCircle color="success" />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card elevation={1} sx={{ p: 1 }}>
              <CardContent sx={{ py: 1 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" variant="caption" gutterBottom>
                      Pending Tax
                    </Typography>
                    <Typography variant="h5" color="warning.main" fontWeight={700} lineHeight={1.2}>
                      {holisticStats.totalPendingTax}
                    </Typography>
                  </Box>
                  <Warning color="warning" />
                </Box>
              </CardContent>
            </Card>
          </Box>
          {/* New: Failed Tax card */}
          <Box>
            <Card elevation={1} sx={{ p: 1 }}>
              <CardContent sx={{ py: 1 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" variant="caption" gutterBottom>
                      Failed Tax
                    </Typography>
                    <Typography variant="h5" color="error.main" fontWeight={700} lineHeight={1.2}>
                      {(holisticStats as any).totalFailedTax || 0}
                    </Typography>
                  </Box>
                  <Warning color="error" />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card elevation={1} sx={{ p: 1 }}>
              <CardContent sx={{ py: 1 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" variant="caption" gutterBottom>
                      Total Amount
                    </Typography>
                    <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
                      {formatCurrency(holisticStats.totalAmount)}
                    </Typography>
                  </Box>
                  <AttachMoney color="primary" />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card elevation={1} sx={{ p: 1 }}>
              <CardContent sx={{ py: 1 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" variant="caption" gutterBottom>
                      Total Tax
                    </Typography>
                    <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
                      {formatCurrency(holisticStats.totalTaxAmount)}
                    </Typography>
                  </Box>
                  <CheckCircle color="success" />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Platform Breakdown - 3 compact cards in a row */}
      {holisticStats && (
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          {[
            { key: 'quickbooks', title: 'QuickBooks Summary' },
            { key: 'shopify', title: 'Shopify Summary' },
            { key: 'csv', title: 'CSV Summary' },
          ].map((p) => (
            <Grid key={p.key} item xs={12} md={4}>
              <Card elevation={1} sx={{ p: 1 }}>
                <CardContent sx={{ py: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {p.title}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 0.5, columnGap: 2 }}>
                    <Typography variant="body2" color="textSecondary">Transactions:</Typography>
                    <Typography variant="body2">{(holisticStats.platformBreakdown as any)[p.key].transactions}</Typography>
                    <Typography variant="body2" color="textSecondary">Tax Calculated:</Typography>
                    <Typography variant="body2">{(holisticStats.platformBreakdown as any)[p.key].taxCalculated}</Typography>
                    <Typography variant="body2" color="textSecondary">Failed:</Typography>
                    <Typography variant="body2">{(holisticStats.platformBreakdown as any)[p.key].failedTax || 0}</Typography>
                    <Typography variant="body2" color="textSecondary">Pending:</Typography>
                    <Typography variant="body2">{(holisticStats.platformBreakdown as any)[p.key].pendingTax}</Typography>
                    <Typography variant="body2" color="textSecondary">Total Amount:</Typography>
                    <Typography variant="body2">{formatCurrency((holisticStats.platformBreakdown as any)[p.key].amount)}</Typography>
                    <Typography variant="body2" color="textSecondary">Total Tax:</Typography>
                    <Typography variant="body2">{formatCurrency((holisticStats.platformBreakdown as any)[p.key].taxAmount)}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Tabs for different views */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}>
          <Tab label="All Transactions" />
          {/* Removed Platform Integrations Tab */}
          <Tab label="Tax Insights" />
          <Tab label="Compliance Status" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* All Transactions View */}
          <Suspense fallback={
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          }>
            <AllTransactions companyId={selectedCompanyId || undefined} />
          </Suspense>
        </TabPanel>

        {/* Removed Platform Integrations TabPanel */}

        <TabPanel value={tabValue} index={1}>
          {/* Tax Insights View */}
          <Typography variant="h6" gutterBottom>
            Tax Calculation Insights
          </Typography>
          
          {holisticStats && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Overall Tax Calculation Progress
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary">
                        Completion Rate
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {holisticStats.totalTransactions > 0 
                          ? Math.round((holisticStats.totalTaxCalculated / holisticStats.totalTransactions) * 100)
                          : 0}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={holisticStats.totalTransactions > 0 
                        ? (holisticStats.totalTaxCalculated / holisticStats.totalTransactions) * 100
                        : 0}
                      color="success"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Tax Collection Summary
                    </Typography>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Total Tax Collected
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {formatCurrency(holisticStats.totalTaxAmount)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Key Insights & Recommendations
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                          <Typography variant="subtitle2" color="success.dark" gutterBottom>
                            Tax calculation status
                          </Typography>
                          <Typography variant="body2" color="success.dark">
                            {holisticStats.totalPendingTax > 0 
                              ? `${holisticStats.totalPendingTax} transactions need tax calculation`
                              : 'All transactions have tax calculated'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                          <Typography variant="subtitle2" color="info.dark" gutterBottom>
                            Data quality
                          </Typography>
                          <Typography variant="body2" color="info.dark">
                            {holisticStats.totalTransactions > 0 
                              ? `${holisticStats.totalTransactions} transactions imported successfully`
                              : 'No transactions imported yet'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                          <Typography variant="subtitle2" color="warning.dark" gutterBottom>
                            Next steps
                          </Typography>
                          <Typography variant="body2" color="warning.dark">
                            {holisticStats.totalPendingTax > 0 
                              ? 'Click "Calculate All Tax" to process pending transactions'
                              : 'Review your tax insights and compliance status'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Compliance Status View */}
          <Typography variant="h6" gutterBottom>
            Tax Compliance Status
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Overall Compliance Score
                  </Typography>
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h2" color="success.main" sx={{ fontWeight: 'bold' }}>
                      {holisticStats && holisticStats.totalTransactions > 0 
                        ? Math.round((holisticStats.totalTaxCalculated / holisticStats.totalTransactions) * 100)
                        : 0}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Tax Calculation Compliance
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Compliance Checklist
                  </Typography>
                  <Box>
                    {[
                      { label: 'Transactions Imported', completed: holisticStats && holisticStats.totalTransactions > 0 },
                      { label: 'Tax Calculations Complete', completed: holisticStats && holisticStats.totalPendingTax === 0 },
                      { label: 'Platform Integrations Active', completed: platformData.length > 0 },
                      { label: 'Data Validation Passed', completed: true }
                    ].map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        {item.completed ? (
                          <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                        ) : (
                          <Warning sx={{ color: 'warning.main', mr: 1, fontSize: 20 }} />
                        )}
                        <Typography variant="body2" color={item.completed ? 'text.primary' : 'text.secondary'}>
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Unified Import Dialog: ImportTransactionsDialog renders its own Dialog, so it
          must not be wrapped in a second one (that mounted two stacked modals). */}
      <ImportTransactionsDialog
        open={integrationDialogOpen}
        initialCompanyId={selectedCompanyId || undefined}
        onCompanyChange={(cid) => {
          setSelectedCompanyId(cid);
            try {
              localStorage.setItem('selectedCompanyId', String(typeof cid === 'number' ? cid : 0));
            } catch {}
            handleRefresh(cid ?? undefined);
          try { window.dispatchEvent(new Event('transactions:refresh')); } catch {}
        }}
        onClose={() => {
          setIntegrationDialogOpen(false);
        }}
        onComplete={() => {
          // refresh stats or other post-import behavior if needed
          handleRefresh();
        }}
        companies={companies}
        loadingCompanies={companiesLoading}
        companyError={companiesError}
        onRequestCompanyRefresh={refreshCompanies}
      />
    </Box>
  );
};

export default CompleteTaxView; 
